import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PendingNavigation {
  mainIdx: number;
  subIdx: number | null;
  route: string;
}

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (_value: boolean) => void;
  showNavigationWarning: boolean;
  setShowNavigationWarning: (_value: boolean) => void;
  pendingNavigation: PendingNavigation | null;
  setPendingNavigation: (_navigation: PendingNavigation | null) => void;
  onSaveChanges: (() => Promise<void>) | null;
  setOnSaveChanges: (_callback: (() => Promise<void>) | null) => void;
  onLeaveWithoutSaving: (() => void) | null;
  setOnLeaveWithoutSaving: (_callback: (() => void) | null) => void;
  registerUnsavedChanges: (
    _hasChanges: boolean,
    _saveCallback?: () => Promise<void>,
    _discardCallback?: () => void,
  ) => void;
  clearUnsavedChanges: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [onSaveChanges, setOnSaveChangesState] = useState<(() => Promise<void>) | null>(null);
  const [onLeaveWithoutSaving, setOnLeaveWithoutSavingState] = useState<(() => void) | null>(null);

  // Wrapper to handle function state correctly
  const setOnSaveChanges = useCallback((callback: (() => Promise<void>) | null) => {
    setOnSaveChangesState(() => callback);
  }, []);

  const setOnLeaveWithoutSaving = useCallback((callback: (() => void) | null) => {
    setOnLeaveWithoutSavingState(() => callback);
  }, []);

  const registerUnsavedChanges = useCallback(
    (hasChanges: boolean, saveCallback?: () => Promise<void>, discardCallback?: () => void) => {
      setHasUnsavedChanges(hasChanges);
      if (saveCallback) {
        setOnSaveChanges(saveCallback);
      }
      if (discardCallback) {
        setOnLeaveWithoutSaving(discardCallback);
      }
    },
    [setOnSaveChanges, setOnLeaveWithoutSaving],
  );

  const clearUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false);
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    setOnSaveChanges(null);
    setOnLeaveWithoutSaving(null);
  }, [setOnSaveChanges, setOnLeaveWithoutSaving]);

  const value: UnsavedChangesContextType = {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    showNavigationWarning,
    setShowNavigationWarning,
    pendingNavigation,
    setPendingNavigation,
    onSaveChanges,
    setOnSaveChanges,
    onLeaveWithoutSaving,
    setOnLeaveWithoutSaving,
    registerUnsavedChanges,
    clearUnsavedChanges,
  };

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
};

export const useUnsavedChanges = (): UnsavedChangesContextType => {
  const context = useContext(UnsavedChangesContext);
  if (context === undefined) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
  }
  return context;
};

export default UnsavedChangesContext;
